import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStore } from '../stores/auth.store';
import { generateRequestHeaders } from '../utils/api-security.util';

const toBearerAuthorization = (value: string | null | undefined) => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  if (!normalized) return null;
  return /^Bearer\s+/i.test(normalized) ? normalized : `Bearer ${normalized}`;
};

/**
 * 🔐 Enterprise Auth Interceptor (Tự động gắn Token)
 * - Tự động lấy JWT Token từ AuthStore và đính kèm vào header `Authorization: Bearer <token>`
 * - An toàn cho SSR nhờ check isPlatformBrowser
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(AuthStore);
  const platformId = inject(PLATFORM_ID);

  // Chỉ gắn Token khi ở môi trường trình duyệt
  if (!isPlatformBrowser(platformId)) {
    return next(req);
  }

  const existingAuthorization = toBearerAuthorization(
    req.headers.get('Authorization'),
  );
  const tokenAuthorization = toBearerAuthorization(store.token());
  const authorization = existingAuthorization || tokenAuthorization;

  return from(
    generateRequestHeaders(
      environment.clientType,
      environment.secretConfig,
      authorization,
    ),
  ).pipe(
    switchMap((sec) => {
      const authReq = req.clone({
        setHeaders: {
          Authorization: sec.authorization,
          'X-Client-Type': sec.clientType,
          'X-Timestamp-Slice': sec.timestampSlice,
          'X-Secret': sec.secret,
        },
      });

      return next(authReq);
    }),
  );
};
